import logger from '../../log';
import KollectiveXHR from '../../interceptor/KollectiveXHR';
import { isManifestUrl } from '../../common/utils/sourceTypeUtils';
import { maybeDecode } from '../../common/utils/stringify';
import { InterceptArgs } from '../bitmovin/interfaces';
import {
  BitmovinHttpResponse,
  OptionalBitmovinRequestController
} from '@kollective-frontend/ksdk-api';
import { send, toHeaders } from './binderFunctions';

const log = logger.getLogger(__filename);

type UnknownArgFunction = (...args: unknown[]) => void;

export interface KollectiveResponse {
  target: XMLHttpRequest;
  currentTarget: KollectiveXHR;
  elapsedTime: number;
  timeToFirstByte: number;
}

// See: https://bitmovin.com/docs/player/api-reference/web/web-sdk-api-reference-v8#/player/web/8/docs/interfaces/core_network.networkconfig.html#sendhttprequest
const intercept = (args: InterceptArgs): OptionalBitmovinRequestController => {
  // We don't currently use type, to have separate behavior for different
  // request types but it is available.
  const { options, request } = args;
  log.debug('Using KollectiveXHR for request', { request, options });
  let timeToFirstByte = -1;
  const start = performance.now();
  const kollectiveXhr = new KollectiveXHR(options);
  if (request.headers) {
    Object.entries(request.headers).forEach(([key, header]) => {
      // The Fetch API automatically normalizes outgoing header keys to
      // lowercase. For consistency's sake, do it here too.
      const lowercasedKey = key.toLowerCase();
      kollectiveXhr.setRequestHeader(lowercasedKey, header);
    });
  }
  const responsePromise = new Promise<XMLHttpRequest>((resolve, reject) => {
    kollectiveXhr.addEventListener('abort', () => {
      reject();
    });
    kollectiveXhr.addEventListener('loadend', (res: Event) => {
      log.debug('loadend:', { request, res });
      const target = (res.target as KollectiveXHR) || kollectiveXhr;
      if (isManifestUrl(request.url)) {
        // For some reason we're getting an ArrayBuffer from the interceptor but
        // bitmovin expects a string.
        target.response = maybeDecode(target.response).data;
      }
      // we cast here because KollectiveXHR implements this values
      resolve((res.target || kollectiveXhr) as XMLHttpRequest);
    });
  });

  kollectiveXhr.addEventListener('loadstart', () => {
    timeToFirstByte = (performance.now() - start) / 1000;
  });

  send(request, kollectiveXhr);

  return {
    getResponse: async (): Promise<BitmovinHttpResponse> => {
      log.debug('Used KollectiveXHR for request', request);
      const res = await responsePromise;
      return {
        body: res.response,
        elapsedTime: (performance.now() - start) / 1000,
        headers: toHeaders(res),
        request,
        status: res.status,
        statusText: res.statusText,
        timeToFirstByte,
        url: request.url
      };
    },
    setProgressListener: (f: UnknownArgFunction): void => {
      kollectiveXhr.addEventListener('progress', f);
    },
    cancel: () => {
      kollectiveXhr.abort();
    }
  };
};

export default intercept;
